import React, { useState, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { fetchTotalScore, generateUserId } from '../../features/games/bicycleGameSlice';
import theme from '../../styles/themes/default';
import { keyframes } from '@mui/system';
import copite from '../../assets/images/bicycle_game/ecran2_copite.png'
import sageata from '../../assets/images/bicycle_game/ecran2_sageata.png'

// Define the flip animation keyframes
const flipAnimation = keyframes`
  0% {
    transform: rotateX(0);
  }
  50% {
    transform: rotateX(-90deg);
  }
  100% {
    transform: rotateX(0);
  }
`;

interface CommunityCounterProps {
  onStartGame?: () => void;
}

const CommunityCounter: React.FC<CommunityCounterProps> = ({ onStartGame }) => {
  const dispatch = useAppDispatch();
  const { totalAllScore, user } = useAppSelector((state) => state.bicycleGame);
  const [previousScore, setPreviousScore] = useState('000');
  const [animatingDigits, setAnimatingDigits] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // Format the score as a 3-digit string
  const formattedScore = totalAllScore.toString().padStart(3, '0');
  const kmDigits = formattedScore.split('');

  useEffect(() => {
    const fetchTeamScore = () => {
      dispatch(fetchTotalScore());
    };

    fetchTeamScore();
    const teamScoreInterval = setInterval(fetchTeamScore, 30000);

    return () => {
      clearInterval(teamScoreInterval);
    };
  }, [dispatch]);

  useEffect(() => {
    if (previousScore !== formattedScore) {
      // Find which digits have changed
      const changedDigits: number[] = [];
      for (let i = 0; i < formattedScore.length; i++) {
        if (previousScore[i] !== formattedScore[i]) {
          changedDigits.push(i);
        }
      }
      
      // Set which digits should animate
      setAnimatingDigits(changedDigits);
      
      // Clear animation state after animation completes
      const timer = setTimeout(() => {
        setAnimatingDigits([]);
        // Update previous score after animation completes
        setPreviousScore(formattedScore);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [formattedScore, previousScore]);

  // Funcție comună pentru a obține sau genera un ID
  const getOrGenerateUserId = async (): Promise<string> => {
    // Verificăm mai întâi în localStorage
    const storedUserId = localStorage.getItem('bicycleGameUserId');
    if (storedUserId) {
      return storedUserId;
    }
    
    // Apoi verificăm în starea Redux
    if (user) {
      // Salvăm ID-ul din Redux și în localStorage pentru consistență
      localStorage.setItem('bicycleGameUserId', user);
      return user;
    }
    
    // Dacă nu există nicăieri, generăm unul nou
    try {
      const newUserId = await dispatch(generateUserId()).unwrap();
      if (!newUserId) {
        throw new Error('Could not generate user ID');
      }
      
      // Salvăm noul ID în localStorage
      localStorage.setItem('bicycleGameUserId', newUserId);
      return newUserId;
    } catch (error) {
      console.error('Error generating user ID:', error);
      throw error;
    }
  };

  const handleStartGame = async () => {
    setLoading(true);
    try {
      // Obținem sau generăm ID-ul utilizatorului
      const userId = await getOrGenerateUserId();
      
      if (onStartGame) {
        onStartGame();
      } 
    } catch (error) {
      console.error('Error starting game:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      minHeight="60vh" 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      sx={{ 
        backgroundColor: '#e9d4a8',
        padding: 4,
        fontFamily: theme.fonts.apex
      }}
    >
      <Box 
        sx={{ 
          maxWidth: 600, 
          width: '100%', 
          textAlign: 'center', 
          position: 'relative',
          padding: 2
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            color: '#3e2e1a', 
            textTransform: 'uppercase',
            marginBottom: 4,
            fontSize: { xs: '1.8rem', sm: '2.5rem' },
            lineHeight: 1.2,
            fontFamily: theme.fonts.poplar
          }}
        >
          ÎMPREUNĂ ILUMINĂM PARCURI DIN COMUNITATEA TA
        </Typography>
        
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#3e2e1a', 
            marginBottom: 2
          }}
        >
          COMUNITATEA A STRÂNS:
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            marginY: 2
          }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', marginRight: 1 }}>
            <Box
              component="img"
              src={copite}
              alt="Kozel copite"
              sx={{ 
                height: '20px',
                width: 'auto',
                marginRight: '5px'
              }}
            />
          </Box>
          
           
          {/* Afișarea contoarelor ca în design cu aspect de flip clock */}
          <Box sx={{ display: 'flex' }}>
            {kmDigits.map((digit, index) => (
              <Box 
                key={index}
                sx={{
                  position: 'relative',
                  width: { xs: 45, sm: 60 },
                  height: { xs: 60, sm: 80 },
                  margin: { xs: '0 2px', sm: '0 5px' },
                  borderRadius: '8px',
                  overflow: 'visible',
                }}
              >
                {/* Panoul principal cu cifra */}
                <Box 
                   sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#3e2e1a',
                    color: 'white',
                    fontSize: { xs: '3.5rem', sm: '5rem' },
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
                    fontFamily: theme.fonts.apex,
                    paddingTop: { xs: '5px', sm: '10px' },
                    animation: animatingDigits.includes(index) 
                      ? `${flipAnimation} 0.7s ease-in-out` 
                      : 'none',
                    transformStyle: 'preserve-3d',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      zIndex: 3,
                    }
                  }}
                >
                  {digit}
                  
                  {/* Linia pe mijloc */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      width: '100%',
                      height: '2px',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      zIndex: 10,
                    }}
                  />
                </Box>
                
                {/* Elementele laterale stânga */}
                <Box 
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '85%',
                    transform: 'translateY(-50%)',
                    width: { xs: 4, sm: 6 },
                    height: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    zIndex: 5,
                  }}
                >
                  <Box sx={{ width: '100%',
                              height: 12,
                              backgroundColor: '#3e2e1a',
                              borderRadius: '2px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                              border: '1px solid #2a1f12'
                              }} />  
                </Box>
                
                {/* Elementele laterale dreapta */}
                <Box 
                  sx={{
                    position: 'absolute',
                    right: 1,
                    top: '85%',
                    transform: 'translateY(-50%)',
                    width: { xs: 4, sm: 6 },
                    height: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    zIndex: 5,
                  }}
                >
                  <Box sx={{ 
                     width: '100%',
                     height: 12,
                      backgroundColor: '#3e2e1a',
                      borderRadius: '2px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                      border: '1px solid #2a1f12'
                      }} />
                </Box>
              </Box>
            ))}
          </Box>
          
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', marginLeft: 1,  }}>
          <Box
            component="img"
            src={copite}
            alt="Kozel copite"
            sx={{ 
              height: '20px',
              width: 'auto',
              marginRight: '5px'
            }}
          />
          </Box>
        </Box>
        
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#3e2e1a', 
            fontWeight: 700,
            textTransform: 'uppercase',
            marginTop: 5,
            //marginBottom: 3
          }}
        >
          Kilometri
        </Typography>
        
       {/* Buton Start Joc */}
      {/* <Button
        onClick={handleStartGame}
        disabled={loading}
        sx={{
          backgroundColor: theme.colors.dark_chocolate,
          fontWeight: theme.typography.fontWeightBold,
          fontFamily: theme.fonts.apex,
          marginBottom: '10px', 
          color: '#D1A85E',
          width: {
              xs: '75%',
              md: '40%'
            },
          alignSelf: 'center',
          fontSize: 'max(2.5vh,2.5vw)',
          '&:hover': {
            backgroundColor: 'white',
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e9d4a8' }}>
          START JOC
        </Typography>
      </Button> */}

      {/* Container for text and arrow */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        marginTop: 1,
        position: 'relative',
        width: '100%'
      }}>

        {/* Arrow image */}
        <Box
          component="img"
          src={sageata}
          alt="Săgeată"
          sx={{ 
            height: { xs: '40px', sm: '70px', md: '150px' },
            position: { sm: 'absolute' },
            right: { sm: '-60px', md: '-100px' },
            marginLeft: { xs: 2, sm: 0 },
            display: { xs: 'none', sm: 'block' },
            zIndex: 1
          }}
        />
      </Box>
    </Box>
  </Box>
  );
};

export default CommunityCounter;